import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../api/client';
import EndpointList from './EndpointList';
import EndpointPerformanceChart from './EndpointPerformanceChart';
import ProjectStats from './ProjectStats';
import Loader from '../Common/Loader';
import ErrorMessage from '../Common/ErrorMessage';
import {
    trackProjectPageView,
    trackNewTestClick,
    trackEndpointClick,
} from '../../utils/analytics';

const ProjectPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [endpoints, setEndpoints] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchProjectData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const [projectRes, endpointsRes, statsRes] = await Promise.all([
                projectsAPI.getById(projectId),
                projectsAPI.getEndpoints(projectId, currentPage, 10),
                projectsAPI.getStats(projectId),
            ]);

            setProject(projectRes.data.project);
            setEndpoints(endpointsRes.data.endpoints);
            setTotalPages(endpointsRes.data.pagination.pages);
            setStats(statsRes.data.stats);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load project data');
        } finally {
            setLoading(false);
        }
    }, [projectId, currentPage]);

    useEffect(() => {
        fetchProjectData();
    }, [fetchProjectData]);

    useEffect(() => {
        trackProjectPageView(projectId);
    }, [projectId]);

    if (loading) {
        return <Loader message="Loading project..." />;
    }

    if (error) {
        return (
            <ErrorMessage
                message={error}
                onRetry={fetchProjectData}
            />
        );
    }

    return (
        <div className="fade-in">
            <style>{`
        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          gap: 16px;
        }
        
        .project-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 36px;
          font-weight: 700;
          font-style: normal;
          margin-bottom: 5px;
          color: var(--text-primary);
          word-break: break-word;
        }
        
        .section-container {
          margin-top: 40px;
        }
        
        .section-title {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 20px;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        @media (max-width: 768px) {
          .project-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .project-title {
            font-size: 28px;
          }
          
          .project-header .btn {
            width: 100%;
            text-align: center;
          }
          
          .section-container {
            margin-top: 30px;
          }
        }
        
        @media (max-width: 480px) {
          .project-title {
            font-size: 24px;
          }
        }
      `}</style>

            <div className="project-header">
                <div>
                    <button
                        className="btn btn-secondary btn-small"
                        onClick={() => navigate('/dashboard')}
                        style={{ marginBottom: '10px' }}
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="project-title">{project?.name}</h1>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => { trackNewTestClick(projectId); navigate(`/project/${projectId}/test/new`); }}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    + New Test
                </button>
            </div>

            {stats && <ProjectStats stats={stats} />}

            {endpoints.length > 0 && <EndpointPerformanceChart endpoints={endpoints} />}

            <div className="section-container">
                <h2 className="section-title">Tested Endpoints</h2>
                <EndpointList
                    endpoints={endpoints}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    onEndpointClick={(endpointId) => { trackEndpointClick(endpointId); navigate(`/endpoint/${endpointId}`); }}
                />
            </div>
        </div>
    );
};

export default ProjectPage;
